import * as React from "react"
import { cn } from "@/lib/utils"

export interface SegmentedControlProps
  extends React.HTMLAttributes<HTMLDivElement> {
  value: string
  onValueChange: (value: string) => void
  children: React.ReactNode
}

export interface SegmentedControlItemProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  value: string
  children: React.ReactNode
  badge?: React.ReactNode
}

const SegmentedControlContext = React.createContext<{
  value: string
  onValueChange: (value: string) => void
} | null>(null)

export function SegmentedControl({
  value,
  onValueChange,
  children,
  className,
  ...props
}: SegmentedControlProps) {
  return (
    <SegmentedControlContext.Provider value={{ value, onValueChange }}>
      <div
        role="group"
        className={cn(
          "inline-flex h-10 items-center justify-center bg-[#f1f4f2] text-muted-foreground p-1 rounded-full w-full max-w-full",
          className
        )}
        {...props}
      >
        {children}
      </div>
    </SegmentedControlContext.Provider>
  )
}

export function SegmentedControlItem({
  value,
  children,
  badge,
  className,
  ...props
}: SegmentedControlItemProps) {
  const context = React.useContext(SegmentedControlContext)

  if (!context) {
    throw new Error(
      "SegmentedControlItem must be used within a SegmentedControl"
    )
  }

  const { value: selectedValue, onValueChange } = context
  const isActive = selectedValue === value

  return (
    <button
      type="button"
      role="radio"
      aria-checked={isActive}
      className={cn(
        "inline-flex items-center justify-center whitespace-nowrap px-3 py-1.5 captionmedium font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 rounded-full flex-1",
        isActive
          ? "bg-white text-foreground shadow-sm"
          : "hover:bg-muted/80",
        className
      )}
      onClick={() => onValueChange(value)}
      {...props}
    >
      {children}
      {badge && badge}
    </button>
  )
}
